import { useCallback, useState } from 'react';

/**
 * Marks the app shell's content pane — the region between the fixed sidebar
 * and the top bar that routed pages render into (`App.tsx`).
 */
export const APP_CONTENT_PANE_ATTR = 'data-app-content-pane';
/** Spread onto the pane element: `<div {...APP_CONTENT_PANE_PROPS}>`. */
export const APP_CONTENT_PANE_PROPS = { [APP_CONTENT_PANE_ATTR]: '' } as const;

/**
 * The content pane enclosing a component, for popovers that must stay
 * inside it.
 *
 * Base UI positions a portalled popup against the viewport, but the sidebar
 * and top bar are fixed chrome painted over the viewport's edges: a popup
 * that "fits" in the viewport can still land under the sidebar. Passing the
 * pane as the popup's `collisionBoundary` makes it flip or shift as if the
 * chrome were a wall.
 *
 * Returns `[pane, anchorRef]`: attach `anchorRef` to any element the
 * component renders and `pane` resolves to its enclosing pane once mounted.
 * `undefined` (a page rendered outside the shell) leaves Base UI's default
 * viewport behaviour in place.
 */
export function useAppContentPane(): [Element | undefined, (el: Element | null) => void] {
  const [pane, setPane] = useState<Element>();
  const anchorRef = useCallback((el: Element | null) => {
    setPane(el?.closest(`[${APP_CONTENT_PANE_ATTR}]`) ?? undefined);
  }, []);
  return [pane, anchorRef];
}
